import { useId } from 'react'
import { BareModal, Icon } from '@cb/apricot-react'
import { getAge } from '@myap/ui-library'
import useUserSettingsQuery from '../../../hooks/useUserSettingsQuery'
import { useAnalyticsStudentProfileAndRegTabEvent } from '../../../hooks/analyticsHooks'
import RegistrationForm from './RegistrationForm'
import ProfileForm from './ProfileForm'
import { adjustModalHeight } from '../../../utilities/dom'
import { BFS_MINIMUM_AGE, BIG_FUTURE, PROFILE, REGISTRATION } from './constants'
import BigFutureForm from './BigFutureForm'
import useStudentInfoAndRegistrationQuery from '../hooks/useStudentInfoAndRegistrationQuery'

const ID = 'studentProfileAndRegistrationModal'

function SelectedFormRenderer({ selectedForm, adjustHeight }) {
  switch (selectedForm) {
    case PROFILE:
      return <ProfileForm withHeader={false} adjustHeight={adjustHeight} />
    case REGISTRATION:
      return <RegistrationForm withHeader={false} adjustHeight={adjustHeight} />
    case BIG_FUTURE:
      return <BigFutureForm withHeader={false} adjustHeight={adjustHeight} />
    default:
      return null
  }
}

function UpdatePersonalInformationModal() {
  // Get user details
  const {
    educationPeriod: {
      code: educationPeriod,
      isRegistered: hasCurrentYearRegistration,
      hasBfsEligibleEnrollments,
    },
  } = useUserSettingsQuery()
  // Get student info and registration
  const { data } = useStudentInfoAndRegistrationQuery({
    educationPeriod,
  })

  const modalHeaderId = useId()
  const [selectedForm, setSelectedForm] = useState(
    hasCurrentYearRegistration ? PROFILE : REGISTRATION
  )
  const [open, setOpen] = useState(true)

  useAnalyticsStudentProfileAndRegTabEvent({ tab: selectedForm })

  const isStandAlone = false
  const useAnimation = true

  const changeForm = e => setSelectedForm(e.target.value)

  const adjustHeight = () => adjustModalHeight(`#${ID}`)

  return (
    <BareModal
      modalId={ID}
      open={open}
      openAnimation={useAnimation}
      closeAnimation={useAnimation}
      clickOverlayToClose={!isStandAlone}
      escClose={!isStandAlone}
      shadowRoot
      modalNode
      onClose={() => setOpen(false)}
    >
      <div className="cb-modal-header cb-modal-has-close">
        <h2 className="cb-modal-title" id={`apricot_modal_header_${modalHeaderId}`}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div className="cb-margin-bottom-8">My AP Profile</div>
            {hasCurrentYearRegistration ? (
              <div
                className="cb-toggles-btn"
                role="radiogroup"
                aria-label="View AP Profile and AP Registration forms"
                style={{ width: '320px' }}
              >
                <label htmlFor="profileToggle" style={{ width: '160px', lineHeight: '24px' }}>
                  <input
                    type="radio"
                    id="profileToggle"
                    name="profileRegistrationToggle"
                    value={PROFILE}
                    onChange={changeForm}
                    checked={selectedForm === PROFILE}
                  />
                  <span style={{ width: '160px', fontSize: '18px' }}>AP Profile</span>
                </label>
                <label htmlFor="registrationToggle" style={{ width: '160px', lineHeight: '24px' }}>
                  <input
                    type="radio"
                    id="registrationToggle"
                    name="profileRegistrationToggle"
                    value={REGISTRATION}
                    onChange={changeForm}
                    checked={selectedForm === REGISTRATION}
                  />
                  <span style={{ width: '160px', fontSize: '18px' }}>AP Registration</span>
                </label>
                {hasBfsEligibleEnrollments && data?.dob && getAge(data.dob) >= BFS_MINIMUM_AGE && data?.bfsDomestic && (
                  <label htmlFor="bfsToggle" style={{ flex: 1.5, lineHeight: '24px', minWidth: 0 }}>
                    <input
                      type="radio"
                      id="bfsToggle"
                      name="profileRegistrationToggle"
                      value={BIG_FUTURE}
                      onChange={changeForm}
                      checked={selectedForm === BIG_FUTURE}
                    />
                    <span style={{ width: '160px', fontSize: '18px' }}>Engage With BFS</span>
                  </label>
                )}
              </div>
            ) : null}
          </div>
        </h2>
        <button
          type="button"
          className="cb-btn cb-btn-square cb-btn-greyscale cb-btn-close"
          aria-describedby={`apricot_modal_header_${modalHeaderId}`}
          data-cb-modal-close="true"
        >
          <span />
          <Icon name="x-mark" />
          <span className="sr-only">Close Modal</span>
        </button>
      </div>
      {open ? (
        <SelectedFormRenderer selectedForm={selectedForm} adjustHeight={adjustHeight} />
      ) : (
        <div className="cb-modal-content">
          <span className="sr-only">modal content</span>
        </div>
      )}
    </BareModal>
  )
}

UpdatePersonalInformationModal.displayName = 'Update Personal Information Modal'

export default UpdatePersonalInformationModal
